import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Droplets, Sun, Info, Calendar, Plus, Sparkles, Camera } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { toast } from "@/hooks/use-toast";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const POPULAR_PLANTS = [
    { name: "Snake Plant (Sansevieria)", wateringFrequency: 3, wateringUnit: "weeks", sunlight: "low", notes: "Extremely hardy. Tolerates low light and neglect. Avoid overwatering." },
    { name: "Fiddle Leaf Fig", wateringFrequency: 1, wateringUnit: "weeks", sunlight: "partial", notes: "Prefers consistent bright indirect light. Water when top soil is dry. Keep away from drafts." },
    { name: "Monstera Deliciosa", wateringFrequency: 1, wateringUnit: "weeks", sunlight: "partial", notes: "Thrives in warm, humid conditions. Water thoroughly and allow to dry slightly between waterings." },
    { name: "Pothos (Devil's Ivy)", wateringFrequency: 1, wateringUnit: "weeks", sunlight: "partial", notes: "Very easy care. Vines can grow quite long. Water when leaves start to droop slightly." },
    { name: "Spider Plant", wateringFrequency: 1, wateringUnit: "weeks", sunlight: "partial", notes: "Prefers bright indirect light and damp but well-drained soil. Produces baby plantlets." },
    { name: "ZZ Plant (Zamioculcas)", wateringFrequency: 3, wateringUnit: "weeks", sunlight: "low", notes: "Thrives on neglect. Drought tolerant. Needs very little light to survive." },
    { name: "Peace Lily", wateringFrequency: 1, wateringUnit: "weeks", sunlight: "low", notes: "Will droop dramatically when thirsty, indicating it needs water. Prefers moist soil." },
    { name: "Aloe Vera", wateringFrequency: 3, wateringUnit: "weeks", sunlight: "full", notes: "Prefers bright direct sunlight. Drought tolerant. Water deeply but infrequently." },
    { name: "Boston Fern", wateringFrequency: 4, wateringUnit: "days", sunlight: "partial", notes: "Requires high humidity. Keep soil consistently moist. Mist regularly." },
    { name: "Jade Plant (Succulent)", wateringFrequency: 2, wateringUnit: "weeks", sunlight: "full", notes: "Requires bright light. Water only when the soil is completely dry." },
    { name: "Calathea (Prayer Plant)", wateringFrequency: 5, wateringUnit: "days", sunlight: "partial", notes: "Needs high humidity. Prefers moist but well-drained soil. Avoid direct sunlight." },
    { name: "Swiss Cheese Plant (Monstera Adansonii)", wateringFrequency: 1, wateringUnit: "weeks", sunlight: "partial", notes: "Prefers bright indirect light. Enjoys misting and climbing poles." },
    { name: "English Ivy", wateringFrequency: 1, wateringUnit: "weeks", sunlight: "partial", notes: "Likes cool temperatures and moist soil. Watch out for spider mites." },
    { name: "Chinese Evergreen (Aglaonema)", wateringFrequency: 2, wateringUnit: "weeks", sunlight: "low", notes: "Very forgiving. Does well in low light. Allow soil to dry partially between waterings." },
    { name: "Rubber Plant (Ficus Elastica)", wateringFrequency: 1, wateringUnit: "weeks", sunlight: "partial", notes: "Prefers bright indirect light. Keep soil moist but not soggy. Wipe leaves." },
];

const AddPlantSheet = ({ open, onOpenChange, onSuccess, isLimitReached, initialData = null, onUpdate = null, onDelete = null }) => {
    const { user } = useAuth();
    const { activeProperty } = usePropertyContext();

    const [loading, setLoading] = useState(false);
    const [name, setName] = useState("");
    const [wateringFrequency, setWateringFrequency] = useState("1");
    const [wateringUnit, setWateringUnit] = useState("weeks");
    const [sunlight, setSunlight] = useState("indirect");
    const [notes, setNotes] = useState("");
    const [isPaused, setIsPaused] = useState(false);

    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);

    // Only used for NEW plants or if explicitly resetting watering
    const [isWateredToday, setIsWateredToday] = useState(true);
    const [lastWateredDate, setLastWateredDate] = useState(new Date().toISOString().split('T')[0]);

    // Populate form if initialData exists (Edit Mode)
    useEffect(() => {
        if (open) {
            if (initialData) {
                setName(initialData.name || "");
                setWateringFrequency(initialData.watering_frequency?.toString() || "1");
                setWateringUnit(initialData.watering_unit || "weeks");
                const dbSunlight = initialData.sunlight_preference || "indirect";
                const uiSunlight = 
                    dbSunlight === "direct" ? "full" : 
                    dbSunlight === "indirect" ? "partial" : 
                    dbSunlight === "shade" ? "low" : dbSunlight;
                
                setSunlight(uiSunlight);
                setNotes(initialData.notes || "");
                setIsPaused(initialData.is_paused || false);
                // Edit mode doesn't reset watering schedule by default
                setIsWateredToday(true);
            } else {
                resetForm();
            }
        }
    }, [initialData, open]);

    const handleFocus = (e) => {
        setTimeout(() => {
            e.target.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 350);
    };

    const resetForm = () => {
        setName("");
        setWateringFrequency("1");
        setWateringUnit("weeks");
        setSunlight("indirect");
        setNotes("");
        setIsPaused(false);
        setIsWateredToday(true);
        setLastWateredDate(new Date().toISOString().split('T')[0]);
        setShowSuggestions(false);
        setFilteredSuggestions([]);
    };

    const handleInputChange = (val) => {
        setName(val);
        if (val.trim()) {
            const matches = POPULAR_PLANTS.filter(plant => 
                plant.name.toLowerCase().includes(val.toLowerCase())
            );
            setFilteredSuggestions(matches);
            setShowSuggestions(true);
        } else {
            setFilteredSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSelectPlant = (plant) => {
        setName(plant.name);
        setWateringFrequency(plant.wateringFrequency.toString());
        setWateringUnit(plant.wateringUnit);
        setSunlight(plant.sunlight);
        setNotes(plant.notes);
        setShowSuggestions(false);
        
        toast({
            title: "Plant Auto-filled",
            description: `Default watering schedule and care notes loaded for ${plant.name}.`,
        });
    };

    const handleSave = async () => {
        if (!name.trim()) {
            toast({ title: "Name Required", description: "Please enter a name for your plant.", variant: "destructive" });
            return;
        }

        if (!initialData && isLimitReached) {
            toast({
                title: "Limit Reached",
                description: "Free tier is limited to 2 plants. Upgrade to Essential or Professional for unlimited garden tracking!",
                variant: "destructive"
            });
            return;
        }

        setLoading(true);
        try {
            // Edit Mode
            if (initialData && onUpdate) {
                const dbSunlight = 
                    sunlight === "full" ? "direct" : 
                    sunlight === "partial" ? "indirect" : 
                    sunlight === "low" ? "shade" : sunlight;

                const updates = {
                    name: name.trim(),
                    watering_frequency: parseInt(wateringFrequency),
                    watering_unit: wateringUnit,
                    sunlight_preference: dbSunlight,
                    notes: notes.trim() || null,
                    is_paused: isPaused,
                };

                await onUpdate(initialData.id, updates);
                onOpenChange(false);
                onSuccess?.();
                return;
            }

            // Create Mode
            const lastWatered = isWateredToday ? new Date() : new Date(lastWateredDate);
            lastWatered.setUTCHours(0, 0, 0, 0);

            const nextWatered = new Date(lastWatered);
            const freq = parseInt(wateringFrequency);

            if (wateringUnit === 'days') {
                nextWatered.setUTCDate(nextWatered.getUTCDate() + freq);
            } else {
                nextWatered.setUTCDate(nextWatered.getUTCDate() + (freq * 7));
            }

            const dbSunlight = 
                sunlight === "full" ? "direct" : 
                sunlight === "partial" ? "indirect" : 
                sunlight === "low" ? "shade" : sunlight;

            const { error } = await supabase.from("plants").insert({
                user_id: user.id,
                property_id: activeProperty?.id,
                name: name.trim(),
                watering_frequency: freq,
                watering_unit: wateringUnit,
                sunlight_preference: dbSunlight,
                notes: notes.trim() || null,
                is_paused: isPaused,
                last_watered_at: lastWatered.toISOString(),
                next_watering_at: nextWatered.toISOString()
            });

            if (error) throw error;

            toast({ title: "Plant Added", description: `${name} has been added to your collection.` });
            resetForm();
            onOpenChange(false);
            onSuccess?.();
        } catch (error) {
            console.error("Error saving plant:", error);
            toast({ title: "Error", description: "Failed to save plant.", variant: "destructive" });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = (open) => {
        if (!open) resetForm();
        onOpenChange(open);
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent side="bottom" onOpenAutoFocus={(e) => e.preventDefault()} className="max-h-[90vh] rounded-t-3xl flex flex-col p-0" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
                <SheetHeader className="pb-4 flex-shrink-0 px-6 pt-6">
                    <SheetTitle className="text-xl font-bold text-foreground">{initialData ? "Edit Plant" : "Add House Plant"}</SheetTitle>
                    <SheetDescription className="text-muted-foreground text-sm">
                        {initialData ? "Update your plant's details and care schedule." : "Track your plant's watering schedule and care notes."}
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 space-y-6 pb-4 px-6">

                    {/* Plant Name with Autocomplete Suggestions */}
                    <div className="space-y-2 relative">
                        <Label htmlFor="plant-name">Plant Name *</Label>
                        <div className="relative">
                            <Input
                                id="plant-name"
                                value={name}
                                onChange={(e) => handleInputChange(e.target.value)}
                                onFocus={(e) => {
                                    handleFocus(e);
                                    if (name.trim()) {
                                        const matches = POPULAR_PLANTS.filter(plant => 
                                            plant.name.toLowerCase().includes(name.toLowerCase())
                                        );
                                        setFilteredSuggestions(matches);
                                        setShowSuggestions(true);
                                    }
                                }}
                                onBlur={() => {
                                    setTimeout(() => {
                                        setShowSuggestions(false);
                                    }, 250);
                                }}
                                placeholder="e.g. Fiddle Leaf Fig"
                                className="rounded-xl pr-8"
                                autoComplete="off"
                            />
                            {name.trim() && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <Sparkles className="w-4 h-4 text-primary/40 animate-pulse" />
                                </div>
                            )}
                        </div>

                        {/* Suggestions Dropdown */}
                        <AnimatePresence>
                            {showSuggestions && filteredSuggestions.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: -8, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -8, scale: 0.97 }}
                                    transition={{ duration: 0.15, ease: "easeOut" }}
                                    className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 bg-white/95 backdrop-blur-md border border-slate-200 shadow-xl rounded-2xl p-1.5 space-y-0.5 max-h-48 overflow-y-auto"
                                >
                                    {filteredSuggestions.map((plant) => (
                                        <button
                                            key={plant.name}
                                            type="button"
                                            onClick={() => handleSelectPlant(plant)}
                                            className="w-full text-left px-3 py-2 rounded-xl text-sm hover:bg-primary/5 active:bg-primary/10 transition-colors flex items-center justify-between group"
                                        >
                                            <span className="font-semibold text-foreground group-hover:text-primary transition-colors">{plant.name}</span>
                                            <span className="text-[10px] bg-blue-500/10 text-blue-600 px-2 py-0.5 rounded-full font-bold">
                                                Every {plant.wateringFrequency} {plant.wateringUnit}
                                            </span>
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Watering Schedule */}
                    <div className="space-y-3">
                        <Label className="flex items-center gap-2">
                            <Droplets className="w-4 h-4 text-blue-500" />
                            Watering Frequency
                        </Label>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <Input
                                    type="number"
                                    value={wateringFrequency}
                                    onChange={(e) => setWateringFrequency(e.target.value)}
                                    onFocus={handleFocus}
                                    min="1"
                                    className="rounded-xl"
                                />
                            </div>
                            <div className="flex-[1.5]">
                                <Select value={wateringUnit} onValueChange={setWateringUnit}>
                                    <SelectTrigger className="rounded-xl">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="days">Days</SelectItem>
                                        <SelectItem value="weeks">Weeks</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground ml-1 font-medium">
                            Every {wateringFrequency} {wateringFrequency === "1" ? wateringUnit.slice(0, -1) : wateringUnit}
                        </p>
                    </div>

                    {/* Sunlight preference */}
                    <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                            <Sun className="w-4 h-4 text-amber-500" />
                            Sunlight Preference
                        </Label>
                        <Select value={sunlight} onValueChange={setSunlight}>
                            <SelectTrigger className="rounded-xl">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="full">Full Sunlight</SelectItem>
                                <SelectItem value="partial">Partial Sunlight</SelectItem>
                                <SelectItem value="low">Low Light / Shade</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Reminder Setup - Hide if editing as we don't want to accidentally reset schedules */}
                    {!initialData && (
                        <div className="space-y-4 p-4 rounded-2xl bg-primary/5 border border-primary/10">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label className="text-base font-semibold text-foreground">Watered today?</Label>
                                    <p className="text-xs text-muted-foreground">Sets reminder calculation from now</p>
                                </div>
                                <Switch
                                    checked={isWateredToday}
                                    onCheckedChange={setIsWateredToday}
                                />
                            </div>

                            {!isWateredToday && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    className="space-y-2 pt-2 border-t border-primary/10"
                                >
                                    <Label className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase tracking-wider">
                                        <Calendar className="w-3.5 h-3.5" />
                                        When was it last watered?
                                    </Label>
                                    <Input
                                        type="date"
                                        value={lastWateredDate}
                                        onChange={(e) => setLastWateredDate(e.target.value)}
                                        max={new Date().toISOString().split('T')[0]}
                                        className="rounded-xl bg-background border-primary/20 w-full"
                                    />
                                </motion.div>
                            )}
                        </div>
                    )}

                    {/* Notes */}
                    <div className="space-y-2">
                        <Label htmlFor="plant-notes" className="flex items-center gap-2">
                            <Info className="w-4 h-4 text-primary" />
                            Care Instructions / Notes
                        </Label>
                        <Textarea
                            id="plant-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            onFocus={handleFocus}
                            placeholder="e.g. Likes humidity, mist leaves every few days..."
                            className="rounded-xl resize-none"
                            rows={3}
                        />
                    </div>

                    {/* Pause Watering */}
                    <div className="space-y-4 p-4 rounded-2xl bg-slate-50 border border-slate-100 mt-4">
                        <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                                <Label className="text-base font-semibold text-foreground">Pause Watering</Label>
                                <p className="text-xs text-muted-foreground">Stop reminders if plant is dormant or sick</p>
                            </div>
                            <Switch
                                checked={isPaused}
                                onCheckedChange={setIsPaused}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex-shrink-0 pt-4 px-6 flex gap-3">
                    {initialData && onDelete && (
                        <Button
                            variant="destructive"
                            onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
                                    setLoading(true);
                                    const success = await onDelete(initialData.id);
                                    setLoading(false);
                                    if (success) {
                                        onOpenChange(false);
                                    }
                                }
                            }}
                            className="flex-1 rounded-xl py-6 font-bold"
                            disabled={loading}
                        >
                            Delete Plant
                        </Button>
                    )}
                    <Button
                        onClick={handleSave}
                        className={cn("flex-1 rounded-xl py-6 font-bold text-white shadow-lg transition-all active:scale-95", !initialData && isLimitReached && "bg-amber-500 hover:bg-amber-600")}
                        disabled={loading}
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (initialData ? "Update Plant" : "Save Plant")}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default AddPlantSheet;
